// utils/collectionLinks.js
//
// The words for collection links, in one place: what the two switches mean,
// what a revoke leaves behind, what every refusal the link endpoints give
// means to a person, and what the person who asked for a link can already
// see. Surfaces render these and never write their own — a link is read on
// both sides of a pair of collections, and two phrasings of one promise
// ("recipients may keep copies") would drift apart by the second edit.
//
// keepr-api docs/SCHEMA.md § Collection links is the contract.

import { apiErrorCode, apiErrorMessage, formatWait, retryAfterSeconds } from './apiErrors';
import { getMyAccess, roleRank, ROLE_RANK, canManage } from './permissions';
import { isArchiveKind, isGlobal, isPersonal } from './collectionKinds';
import { formatDisplayDate } from './dateDisplay';
import { normalizeId } from './ids';

// ------------------------------------------------------------------ switches

export const COPIES_LABEL = 'Recipients may keep copies';
export const COPIES_DESCRIPTION = 'Off: people in the other collection can read these records here but cannot export them, download their attachments or copy them into their own collection — and if the link ends, they keep only references that say "No longer available". On: they may do all of that, and keep copies of what they reference if the link ends.';

export const KEYS_LABEL = 'Assistants and API keys';
export const KEYS_DESCRIPTION = 'On: the other collection\'s API keys and connected assistants read these records through the link, as its members do. Off: only people signed in to keepr do.';

// ------------------------------------------------------------------ confirmations

/**
 * What ending a link leaves behind, said before it happens. `side` is who is
 * ending it: 'source' revokes (A stops sharing), 'target' removes (B stops
 * reading). The copies clause is the one a manager must not learn afterwards.
 */
export function endLinkConsequence(link, side) {
	const copies = link?.allowCopies === true;
	const other = side === 'source' ? (link?.target?.name || 'the other collection') : (link?.source?.name || 'the other collection');
	const kept = copies
		? `Because recipients may keep copies, ${side === 'source' ? other : 'this collection'} keeps copies of the records it references.`
		: `Where ${side === 'source' ? other : 'this collection'} references these records, the references stay and show "No longer available".`;
	const lead = side === 'source'
		? `People in ${other} stop reading these records on their next page load.`
		: `People in this collection stop reading ${other}'s records on their next page load.`;
	const tail = side === 'source'
		? `Its managers are told. Linking again takes a new request from ${other}, or someone who manages both.`
		: `${other}'s managers are told. Reading it again takes a new request.`;
	return `${lead} ${kept} ${tail}`;
}

// ------------------------------------------------------------------ errors

const LINK_MESSAGES_HOP = 'A shared link can\'t filter through a lookup into another collection: the public page has nobody to read that collection for. Remove that condition, or filter on this collection\'s own fields.';

const LINK_MESSAGES = {
	link_to_self: 'A collection cannot be linked to itself.',
	link_target_not_found: 'That collection can\'t be found, or you don\'t have access to it of your own. Pick another.',
	link_source_not_found: 'That collection can\'t be found, or you don\'t have access to it of your own. Pick another.',
	invalid_link: 'That link can\'t be saved as it is: it covers the whole collection, one card, or a filter — never a card and a filter together — and a note is at most 500 characters.',
	link_from_global: 'A global collection can\'t be linked: everyone can already read it, so a link would add nothing.',
	link_into_personal: 'A personal collection can\'t receive a link — it has one member. Share with that person instead.',
	link_archive: 'A keepr archive can\'t be linked.',
	link_target_archived: 'That collection is archived. It has to be unarchived before it can be linked.',
	link_query_me: 'A link\'s filter can\'t use me: the people reading through a link are the other collection\'s members.',
	link_exists: 'That link already exists.',
	link_request_pending: 'That link has already been requested and is waiting for an answer.',
	link_target_gone: 'The collection that asked no longer exists. Decline the request to clear it.',
	link_busy: 'This link changed while you were saving. Look again and retry.',
	link_request_stale: 'The person who asked no longer manages their collection, or no longer has access to this one of their own, so this request can\'t be approved. Decline it, or ask them to request again.',
	link_indexes_missing: 'Linking is being set up — try again shortly.',
	use_links_endpoint: 'This access is a collection link. Change it under Linked collections.',
	link_needs_request: 'You don\'t manage that collection, so it hasn\'t agreed to this link. One of its managers can request it from their Sharing tab, and you approve it here.',
	link_widen_needs_request: 'That would share more than the other collection agreed to. Its managers have to request the wider link.',
	link_widens_request: 'An approval can narrow what was asked for, never widen it.',
	link_copy_failed: 'The copies this link promised could not be made, so the link was left as it was. Turning "Recipients may keep copies" off lets it end without copies.',
	export_not_allowed: 'The collection these records come from doesn\'t let this one keep copies of them.',
	copies_not_allowed: 'The collection these records come from doesn\'t let this one keep copies of them.',
	card_borrowed: 'This card belongs to a linked collection. Its records are added there, by that collection\'s people — not here.',
	cross_collection_hop_not_allowed: LINK_MESSAGES_HOP,
};

const STATUS_WORDS = { approved: 'approved', declined: 'declined', withdrawn: 'withdrawn', pending: 'reopened' };

/**
 * Any refusal from the link endpoints, as the sentence a person should read.
 * Codes carrying numbers (`limit`, `direction`, `retryAfter`, `status`) put
 * them into the sentence; a 429 reads its wait off Retry-After (or the body's
 * `retryAfter`). Everything else — a network failure, an unverified account,
 * a code this build has not heard of — falls to the shared reader.
 */
export function describeLinkError(error, fallback = 'That didn\'t go through. Try again.') {
	const code = apiErrorCode(error);
	const data = error?.response?.data || {};
	const limit = Number(data.limit);
	const hasLimit = Number.isFinite(limit) && limit > 0;
	const wait = retryAfterSeconds(error);
	const inWait = wait ? `in ${formatWait(wait)}` : 'later';

	switch (code) {
		case 'link_limit':
			if (data.direction === 'incoming') {
				return `That collection already reads from ${hasLimit ? limit : 'as many'} collections — the most it can. One of its links has to end first.`;
			}
			return `This collection already shares ${hasLimit ? `${limit} links` : 'as many links as it can'} — the most it can. Revoke one first.`;
		case 'link_request_limit':
			return `This collection already has ${hasLimit ? `${limit} link requests` : 'as many link requests as it can'} waiting. Withdraw one first.`;
		case 'link_request_source_limit':
			return `That collection already has ${hasLimit ? `${limit} link requests` : 'as many link requests as it can take'} waiting for an answer. Try again once its managers have answered them.`;
		case 'link_request_daily_limit':
			return `You've made ${hasLimit ? `${limit} link requests` : 'as many link requests as you can'} today. You can ask again ${inWait}.`;
		case 'link_request_cooldown':
			return `A request between these two collections was withdrawn or declined recently. You can ask again ${inWait}.`;
		case 'link_request_decided': {
			const word = STATUS_WORDS[data.status];
			return word ? `This request has already been ${word}.` : 'This request has already been answered.';
		}
		default:
			break;
	}
	if (code && LINK_MESSAGES[code]) return LINK_MESSAGES[code];
	return apiErrorMessage(error, fallback);
}

// ------------------------------------------------------------------ queries that cross collections

/**
 * The `warnings` a stored-query writer answers with (keepr-api § KQL, "Stored
 * queries do not cross"): `[{ code: 'cross_collection_hop', path, message }]`
 * for a condition that walks a lookup into another collection's records. One
 * sentence, or null when there is nothing to say. `label` names what was
 * saved; a saved filter (run as each reader's own `?q=`) resolves the hop
 * through that reader's access, everything else matches nothing there.
 */
export function describeQueryWarnings(warnings, label = 'filter') {
	const hops = (Array.isArray(warnings) ? warnings : []).filter(w => w && w.code === 'cross_collection_hop');
	if (!hops.length) return null;
	const paths = [...new Set(hops.map(w => w.path).filter(Boolean))];
	const named = paths.length ? paths.map(p => `"${p}"`).join(', ') : 'A condition';
	const verb = paths.length === 1 ? 'reaches' : 'reach';
	if (label === 'saved filter' || label === 'short link') {
		return `Saved. ${named} ${verb} into another collection's records: people who can't read that collection get no matches for ${paths.length === 1 ? 'it' : 'them'}.`;
	}
	return `Saved. ${named} ${verb} into another collection's records, which a ${label} can't read — ${paths.length === 1 ? 'that condition matches' : 'those conditions match'} nothing.`;
}

/** Why a share link's filter can't walk into another collection, before the API says so. */
export const SHARE_LINK_HOP_MESSAGE = LINK_MESSAGES_HOP;

// ------------------------------------------------------------------ a keep-copy that failed

/** The label of the one fix a failed keep-copy has (keepr-api `hint: 'turn_copies_off'`). */
export const COPY_FAILED_FIX_LABEL = 'Turn copies off and revoke';

/**
 * What to say when ending a link failed its keep-copy (409
 * `link_copy_failed`): the link still stands, and why; then the way out —
 * A's own switch for A's managers, a request to A's managers for B's.
 * `side` is who was ending it ('source' revokes, 'target' removes, 'delete'
 * is A's collection delete).
 */
export function describeCopyFailure(link, side) {
	const source = link?.source?.name || 'the other collection';
	const target = link?.target?.name || 'the other collection';
	if (side === 'target') {
		return {
			title: 'The link was not removed',
			body: `${source} lets this collection keep copies when its link ends, and those copies could not be made — so the link is still here. ${source}'s managers can turn "Recipients may keep copies" off, and then it can be removed without copies. Or try again later.`,
		};
	}
	if (side === 'delete') {
		return {
			title: 'The collection was not deleted',
			body: 'A linked collection may keep copies of what it references here, and those copies could not be made — so nothing was deleted. Untick "Let linked collections keep copies" to delete without them, or try again later.',
		};
	}
	return {
		title: 'The link was not revoked',
		body: `${target} may keep copies of what it references when this link ends, and those copies could not be made — so the link is still in place. Turn copies off to revoke it without them: ${target} then keeps references that say "No longer available".`,
	};
}

// ------------------------------------------------------------------ the requester's reach

const ROLE_REACH = {
	owner: 'They own this collection.',
	manage: 'They manage this collection.',
	write: 'They can edit this whole collection.',
	read: 'They can view this whole collection.',
};

/** 'A', 'A and B', 'A, B and 2 more cards'. */
function nameList(names) {
	if (names.length <= 1) return names[0] || '';
	if (names.length === 2) return `${names[0]} and ${names[1]}`;
	if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
	const more = names.length - 2;
	return `${names[0]}, ${names[1]} and ${more} more card${more === 1 ? '' : 's'}`;
}

/**
 * What the person who ASKED can see of this collection of their own, said
 * plainly, for the manager deciding. `requesterReach` arrives on A's view of
 * a pending request: { scope: 'collection'|'partial'|'none', role, cards,
 * filtered, managesTarget }. `cards` is descendants-included, so a child card
 * whose parent is also listed is left unsaid ("Work orders", not "Work
 * orders, Emergency work orders"). `cardsById` names and relates them.
 *
 * → { sentence, approvable } — `approvable` false when the API would refuse
 * the approval as stale (409 `link_request_stale`).
 */
export function describeRequesterReach(reach, { cardsById = {}, targetName = 'their collection' } = {}) {
	if (!reach || typeof reach !== 'object') return { sentence: null, approvable: true };
	if (reach.managesTarget === false) {
		return { sentence: `They no longer manage ${targetName}, so this request can't be approved.`, approvable: false };
	}
	if (reach.scope === 'collection') {
		return { sentence: ROLE_REACH[reach.role] || ROLE_REACH.read, approvable: true };
	}
	if (reach.scope === 'partial') {
		const ids = (Array.isArray(reach.cards) ? reach.cards : []).map(normalizeId).filter(Boolean);
		const set = new Set(ids);
		const tops = ids.filter(id => !set.has(normalizeId(cardsById[id]?.parentCardId)));
		const names = tops.map(id => cardsById[id]?.name || null);
		const known = names.filter(Boolean);
		const unknown = names.length - known.length;
		const listed = unknown ? [...known, `${unknown} other card${unknown === 1 ? '' : 's'}`] : known;
		if (listed.length && reach.filtered) {
			return { sentence: `They can see only ${nameList(listed)} in this collection, and the records a filter allows them.`, approvable: true };
		}
		if (listed.length) return { sentence: `They can see only ${nameList(listed)} in this collection.`, approvable: true };
		if (reach.filtered) return { sentence: 'They can see only the records a filter allows them in this collection.', approvable: true };
	}
	return { sentence: 'They no longer have access to this collection of their own, so this request can\'t be approved.', approvable: false };
}

/**
 * Does approving this request give their collection more than the person who
 * asked can see themselves? Only answerable for a partial reach: a whole or
 * filtered request is wider than a card grant, and a card request is wider
 * unless the card is one they already see.
 */
export function requestExceedsReach(request, reach) {
	if (!reach || reach.scope !== 'partial') return false;
	const cardId = normalizeId(request?.card_id);
	if (!cardId) return true;
	return !(reach.cards || []).map(normalizeId).includes(cardId);
}

// ------------------------------------------------------------------ who may pick what

/** May this person list the links of the collection at all? Collection-level read (card/filtered grantees get 403). */
export function canSeeLinks(collection) {
	return roleRank(collection) >= ROLE_RANK.read;
}

/** Why this collection may not share by link, or null (the API's link_from_global / link_archive). */
export function sourceBlockedReason(collection) {
	if (isGlobal(collection)) return 'This is a global collection: everyone can already read it, so it has nothing to link.';
	if (isArchiveKind(collection)) return 'A keepr archive can\'t be linked.';
	return null;
}

/** Why this collection may not receive a link, or null (link_into_personal / link_archive / link_target_archived). */
export function targetBlockedReason(collection) {
	if (isPersonal(collection)) return 'A personal collection can\'t read from other collections by link — it has one member. Ask to be given access to the other collection instead.';
	if (isArchiveKind(collection)) return 'A keepr archive can\'t be linked.';
	if (collection?.status === 'archived') return 'This collection is archived. Unarchive it before linking it to another.';
	return null;
}

/**
 * The collections a link FROM `selfId` may point at: ones the caller also
 * manages (a direct link needs both consents) that could receive a link.
 */
export function linkTargetCandidates(collections, selfId) {
	const self = normalizeId(selfId);
	return (collections || []).filter(collection => {
		const id = normalizeId(collection?._id);
		return id && id !== self && canManage(collection) && !targetBlockedReason(collection);
	});
}

/**
 * The collections `selfId` may ASK to read: ones the caller reads of their
 * OWN — a role, a card grant or a filtered grant; the global flag does not
 * count and a global collection has nothing to link anyway.
 */
export function linkSourceCandidates(collections, selfId) {
	const self = normalizeId(selfId);
	return (collections || []).filter(collection => {
		const id = normalizeId(collection?._id);
		if (!id || id === self || sourceBlockedReason(collection)) return false;
		const access = getMyAccess(collection);
		const queryGrants = collection?.myAccess?.queryGrants;
		return Boolean(access.role)
			|| access.isAdmin
			|| Object.keys(access.cardRoles || {}).length > 0
			|| (Array.isArray(queryGrants) && queryGrants.length > 0);
	});
}

// ------------------------------------------------------------------ notices

/**
 * The platform notices the link endpoints send (keepr-api
 * utils/collectionLinkUtils.js → utils/systemNotify.js) say what they are:
 * `origin: 'system'`, `kind: 'collection-link'` and an `event` — never
 * recognised by their title, which is prose for a person and cut to fit.
 */
export const LINK_NOTICE_KIND = 'collection-link';
export const LINK_NOTICE_EVENTS = Object.freeze(['request', 'approve', 'decline', 'revoke', 'remove', 'copies-on', 'source-deleted']);

/** Is this inbox row a collection-link notice? */
export const isLinkNotice = notification => notification?.origin === 'system' && notification?.kind === LINK_NOTICE_KIND;

export const linkedCollectionsPath = collectionId => `/collections/${normalizeId(collectionId)}/form?tab=sharing#linked-collections`;

/**
 * Where a link notice's reader acts on it — the notified collection's Sharing
 * tab, where requests are answered and links are listed — or null when the
 * row is not a link notice.
 */
export function linkNoticePath(notification) {
	if (!isLinkNotice(notification)) return null;
	const collectionId = normalizeId(notification?.collection_id);
	return collectionId ? linkedCollectionsPath(collectionId) : null;
}

// ------------------------------------------------------------------ history

const HISTORY_VERBS = { revoke: 'Revoked', remove: 'Removed', approve: 'Approved', decline: 'Declined', withdraw: 'Withdrawn' };

// What keepr-api writes for a collection purged with its History (KPR-659), on
// the end row of the side that survives it, in place of a name.
const PURGED_COLLECTION = 'a deleted collection';

/**
 * The name of the OTHER collection of a history entry: its summary's name —
 * live while the caller can read it, else as it was named when it happened
 * (`current: false`, keepr-api collectionLinkHistory) — else, purged, so no
 * summary, its side of the entry's label ("Clinic → Partner", as the two were
 * named then).
 */
export function linkHistoryOtherName(entry) {
	const other = entry?.direction === 'outgoing' ? entry?.target : entry?.source;
	if (other?.name) return other.name;
	const parts = typeof entry?.label === 'string' ? entry.label.split(' → ') : [];
	if (parts.length === 2) return (entry?.direction === 'outgoing' ? parts[1] : parts[0]) || null;
	return null;
}

/**
 * A link history entry (keepr-api GET …/links/history) in words — the same
 * sentences on both sides of a pair, from each side's own entry:
 *
 *   status   the tag: "Link revoked", "Request declined", …
 *   when     what happened and when: "Shared Sep 20, 2026 – Sep 26, 2026",
 *            "Read …", "Asked to read this collection on …", "Asked to read it
 *            on …" — then the answer's date for a request
 *   startedLabel / endedLabel   "Linked by" / "Asked by", "Revoked by" …, for
 *            a person (`startedBy` / `endedBy`, the surface renders them)
 *   endedByText   who ended it when no person is named: the other
 *            collection (its people are theirs to name — keepr-api D9), or
 *            keepr itself (an account deleted, or the collection purged, when
 *            its name is gone from the record too); null when a person is
 *   copies   links only: the switch as it stood when it ended
 */
export function describeLinkHistory(entry) {
	const outgoing = entry?.direction === 'outgoing';
	const isLink = entry?.kind === 'link';
	const verb = HISTORY_VERBS[entry?.event] || 'Ended';
	const date = value => (value ? formatDisplayDate(value) : null);
	const other = linkHistoryOtherName(entry) || 'the other collection';
	const started = date(entry?.startedAt);
	const ended = date(entry?.endedAt);

	let when;
	if (isLink) {
		const lead = outgoing ? 'Shared' : 'Read';
		when = started ? `${lead} ${started} – ${ended}` : `${lead} until ${ended}`;
	} else {
		const asked = outgoing ? 'Asked to read this collection' : 'Asked to read it';
		when = [started ? `${asked} on ${started}` : asked, `${verb} ${ended}`].join(' · ');
	}

	let endedByText = null;
	if (!entry?.endedBy) {
		if (entry?.endedBySide === 'system') {
			const collection = other === PURGED_COLLECTION ? 'that collection' : other;
			endedByText = isLink ? `keepr, when ${collection} was deleted` : 'keepr, when an account behind it was deleted';
		} else if (entry?.endedBySide === 'other') {
			endedByText = other;
		} else {
			endedByText = 'this collection';
		}
	}

	return {
		status: `${isLink ? 'Link' : 'Request'} ${verb.toLowerCase()}`,
		when,
		startedLabel: isLink ? 'Linked by' : 'Asked by',
		endedLabel: `${verb} by`,
		endedByText,
		copies: isLink ? (entry?.allowCopies ? 'Copies allowed' : 'Read-only, no copies') : null,
	};
}
